import React, { useState, useEffect } from 'react'
import { Breadcrumbs, Typography, ThemeProvider } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import UserLocationForm from './announceForms/announceLocationForm'
import UserSchedulesForm from './announceForms/announceSchedulesForm'
import UserPossibilitiesForm from './announceForms/announcePossibilitiesLibertinsForm'
import UserContactForm from './announceForms/announceContactForm'
import theme from '../../themes/theme'
import { useTranslation } from 'react-i18next'
import MyButton from '../../components/buttons'
import ImageUpload from './announceForms/announcePicturesForm'
import {
  postMyAnnounce,
  updateMyAnnounce,
  fetchAnnounceByUserId,
} from '../../../services/announcesLibertins'
import * as Sentry from '@sentry/react'
import UserTitleForm from './announceForms/announceTitle'
import Modal from '../../components/MyModal'
import { getUser } from '../../../services/oneUser'
import { IoIosWarning } from 'react-icons/io'
import {
  validateFormData,
  ValidationError,
} from '../../validators/postAnnounce'
import CookieManager from '../../lib/CookiesManager'
import StepSelector from '../../components/StepSelector'

interface Field {
  type: string
  label: string
  options?: string[]
}

interface Step {
  title: string
  fields: Field[]
}

const FormWizard: React.FC<{ userProfileFormProps: any }> = ({
  userProfileFormProps,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1335)
  const [activeStep, setActiveStep] = useState(() => {
    const stepParam = searchParams.get('step')
    return stepParam ? parseInt(stepParam, 10) : 0
  })
  const [formData, setFormData] = useState(() => {
    const initialData = userProfileFormProps || {}
    const myAnnounceStr = localStorage.getItem('myAnnounce')
    const myAnnounce = myAnnounceStr ? JSON.parse(myAnnounceStr) : null
    return myAnnounce && myAnnounce.hasOwnProperty('nickname')
      ? { ...initialData, ...myAnnounce, status: ['libertins'] }
      : { ...initialData, status: ['libertins'] }
  })
  const [isStepValid, setIsStepValid] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [, setStepValidity] = useState<boolean[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const { t } = useTranslation()
  const [hasExistingAnnounce, setHasExistingAnnounce] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [popupMessage, setPopupMessage] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [submissionStatus, setSubmissionStatus] = useState<
    'success' | 'error' | null
  >(null)
  const navigate = useNavigate()

  const steps: Step[] = [
    {
      title: t('create-ad.location'),
      fields: [{ type: 'input', label: 'location' }],
    },
    {
      title: t('create-ad.schedules'),
      fields: [],
    },
    {
      title: t('create-ad.possibilities'),
      fields: [],
    },
    {
      title: t('create-ad.contact'),
      fields: [],
    },

    {
      title: t('create-ad.announce'),
      fields: [
        { type: 'input', label: 'title' },
        { type: 'textarea', label: 'description' },
      ],
    },
    {
      title: t('create-ad.pictures'),
      fields: [],
    },
  ]

  useEffect(() => {
    const currentStep = searchParams.get('step')
    if (currentStep !== activeStep.toString()) {
      setSearchParams({ step: activeStep.toString() })
    }
  }, [activeStep])

  useEffect(() => {
    const stepParam = searchParams.get('step')
    if (stepParam) {
      const step = parseInt(stepParam, 10)
      if (step >= 0 && step < steps.length) {
        setActiveStep(step)
      }
    }
  }, [searchParams])

  useEffect(() => {
    const validateCurrentStep = () => {
      const isValid = steps[activeStep].fields.every((field: any) => {
        const fieldValue = formData[field.label]
        if (field.label === 'weight') {
          return (
            typeof fieldValue === 'number' &&
            fieldValue >= 40 &&
            fieldValue <= 120
          )
        } else if (field.label === 'height') {
          return (
            typeof fieldValue === 'number' &&
            fieldValue >= 140 &&
            fieldValue <= 220
          )
        } else if (field.label === 'nickname') {
          return typeof fieldValue === 'string' && fieldValue.trim().length > 3
        } else if (field.label === 'title') {
          return typeof fieldValue === 'string' && fieldValue.trim().length > 4
        } else if (field.label === 'description') {
          return (
            typeof fieldValue === 'string' && fieldValue.trim().length > 129
          )
        } else if (field.label === 'languages') {
          return Array.isArray(fieldValue) && fieldValue.length > 0
        } else if (field.label === 'location') {
          return Array.isArray(fieldValue) && fieldValue.length > 0
        } else if (field.label === 'nationality') {
          return typeof fieldValue === 'string' && fieldValue.trim().length > 0
        } else if (field.label === 'status') {
          return Array.isArray(fieldValue) && fieldValue.length > 0
        } else if (
          field.label === 'available247' ||
          field.label === 'customHours'
        ) {
          return formData['available247'] || formData['customHours']
        } else {
          return fieldValue === true || fieldValue === false
            ? true
            : !!fieldValue
        }
      })
      setIsStepValid(isValid)
      setStepValidity(prev => {
        const newValidity = [...prev]
        newValidity[activeStep] = isValid
        return newValidity
      })
    }

    validateCurrentStep()
  }, [activeStep, formData])

  useEffect(() => {
    const fetchUserEmail = async () => {
      const userId = CookieManager.getUserId()
      const token = CookieManager.getToken()

      if (userId && token) {
        try {
          const userData = await getUser(userId, token)
          const userEmail = userData.user.email

          const myAnnounce = JSON.parse(
            localStorage.getItem('myAnnounce') || '{}',
          )
          myAnnounce.email = userEmail
          localStorage.setItem('myAnnounce', JSON.stringify(myAnnounce))
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              action: 'fetchUserEmail',
              userId,
              timestamp: new Date().toISOString(),
            },
            tags: {
              type: 'api_error',
              method: 'fetchUserEmail',
            },
          })
        }
      }
    }

    fetchUserEmail()
  }, [])

  useEffect(() => {
    const fetchExistingAnnounce = async () => {
      const token = CookieManager.getToken()
      const userId = CookieManager.getUserId()

      if (!token || !userId) return

      try {
        const existingAnnounce = await fetchAnnounceByUserId(userId, token)

        if (existingAnnounce) {
          localStorage.setItem('myAnnounce', JSON.stringify(existingAnnounce))
          setFormData(existingAnnounce)
          setHasExistingAnnounce(true)
        }
      } catch (error) {
        console.error('Error fetching announce:', error)
        Sentry.captureException(error, {
          extra: { action: 'fetchExistingAnnounce', userId },
          tags: { type: 'api_error', method: 'fetchExistingAnnounce' },
        })
      }
    }

    const myAnnounceStr = localStorage.getItem('myAnnounce')
    if (myAnnounceStr) {
      const myAnnounce = JSON.parse(myAnnounceStr)
      setFormData((prev: any) => ({
        ...prev,
        ...myAnnounce,
        status: ['libertins'],
      }))
      setHasExistingAnnounce(true)
    } else {
      fetchExistingAnnounce()
    }
  }, [])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const handleBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const newStep = activeStep - 1
    if (newStep >= 0) {
      setActiveStep(newStep)
      setSearchParams({ step: newStep.toString() })
    }
  }

  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const newStep = activeStep + 1
    if (newStep < steps.length) {
      setActiveStep(newStep)
      setSearchParams({ step: newStep.toString() })
    }
  }

  const handleBreadcrumbClick = (index: number) => {
    if (activeStep < index && !isStepValid) {
      return
    }
    setActiveStep(index)
    setSearchParams({ step: index.toString() })
  }
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const token = CookieManager.getToken()
    const userId = CookieManager.getUserId()

    if (!token || !userId) {
      setPopupMessage(t('authentication-required'))
      return
    }

    const errors = validateFormData(formData, t, false)
    if (errors.length > 0) {
      setValidationErrors(errors)
      setShowValidationErrors(true)
      return
    }

    const dataToSend = {
      ...formData,
      userId: parseInt(userId),
      boostedTime: new Date().toISOString(),
      status: ['libertins'],
    }

    try {
      let responseData

      if (hasExistingAnnounce) {
        responseData = await updateMyAnnounce(dataToSend, token, userId)
      } else {
        responseData = await postMyAnnounce(dataToSend, token, userId)
        setHasExistingAnnounce(true)
      }

      if (responseData) {
        const oldAnnounce = JSON.parse(
          localStorage.getItem('myAnnounce') || '{}',
        )

        const updatedFormData = {
          ...oldAnnounce,
          ...responseData,
          practices: responseData.practices?.length
            ? responseData.practices
            : oldAnnounce.practices,
          disponibility: responseData.disponibility?.length
            ? responseData.disponibility
            : oldAnnounce.disponibility,
          availabilityStart: responseData.availabilityStart?.length
            ? responseData.availabilityStart
            : oldAnnounce.availabilityStart,
          availabilityEnd: responseData.availabilityEnd?.length
            ? responseData.availabilityEnd
            : oldAnnounce.availabilityEnd,
        }

        localStorage.setItem('myAnnounce', JSON.stringify(updatedFormData))
        setFormData(updatedFormData)
        setSubmissionStatus('success')
        setPopupMessage(t('create-ad.ad-saved-success'))
      }
    } catch (error) {
      console.error('🚨 Error submitting announce:', error)
      setPopupMessage(t('error-submitting-announce'))
      Sentry.captureException(error, {
        extra: { action: 'handleSubmit', userId },
        tags: { type: 'submit_error', method: 'handleSubmit' },
      })
    } finally {
      setIsModalOpen(true)
    }
  }

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }))
    setHasUnsavedChanges(true)
  }

  const handleClosePopup = () => {
    setPopupMessage(null)
    setSubmissionStatus(null)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    if (submissionStatus === 'success') {
      setSearchParams({})
      navigate('/dashboard/announces-libertin/')
    }
  }

  const renderValidationErrors = () => {
    if (!showValidationErrors || validationErrors.length === 0) return null

    return (
      <div className='bg-redlight p-4 rounded-lg mb-4'>
        <ul className='space-y-2'>
          {validationErrors.map((error, index) => (
            <li
              key={index}
              className='text-anthracite text-left flex items-start'
            >
              <IoIosWarning
                className='text-redpink flex-shrink-0 mt-1 mr-2'
                size={16}
              />
              <span>
                <b>{error.stepName} :</b> {error.message}
              </span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const renderBreadcrumbs = () => {
    if (isMobile) {
      return (
        <StepSelector
          steps={steps}
          activeStep={activeStep}
          onStepSelect={handleBreadcrumbClick}
          isStepValid={isStepValid}
        />
      )
    }

    return (
      <div className='w-full overflow-hidden mb-2'>
        <div className='overflow-x-auto md:overflow-visible '>
          <Breadcrumbs
            aria-label='breadcrumb'
            separator=''
            className='bg-anthracite rounded-lg min-w-fit'
          >
            <ol className='list-none flex w-full bg-white rounded-lg whitespace-nowrap'>
              {steps.map((step, index) => (
                <li
                  key={step.title}
                  className={`breadcrumb-item mr-[-18px] text-[11px] sm:text-xs pr-2 sm:pr-4 md:pr-6 items-center text-center  cursor-pointer py-2 md:py-3 text-xs md:text-sm ${
                    activeStep >= index
                      ? 'bg-redpink text-white'
                      : 'bg-anthracite text-white'
                  }`}
                  style={{
                    clipPath:
                      index !== 0
                        ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)'
                        : 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 0px 50%)',
                    borderRadius:
                      index === 0
                        ? '8px 0 0 8px'
                        : index === steps.length - 1
                        ? '0 8px 8px 0'
                        : 'none',
                    paddingLeft: index === 0 ? '16px' : '32px',
                  }}
                  onClick={() => handleBreadcrumbClick(index)}
                >
                  <Typography>
                    <span className='mr-1 md:mr-2 font-bold'>{index + 1}.</span>
                    {step.title}
                  </Typography>
                </li>
              ))}
            </ol>
          </Breadcrumbs>
        </div>
      </div>
    )
  }

  return (
    <div className='w-full mb-20'>
      {renderBreadcrumbs()}
      {renderValidationErrors()}
      <ThemeProvider theme={theme}>
        <form className='mt-5 mb-5'>
          {activeStep === 0 && (
            <UserLocationForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 1 && (
            <UserSchedulesForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 2 && (
            <UserPossibilitiesForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 3 && (
            <UserContactForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}

          {activeStep === 4 && (
            <UserTitleForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 5 && <ImageUpload />}
          <div className='bottom-0 left-0 fixed  mt-2 w-full bg-white p-2 shadow-lg sm:shadow-none sm:static sm:p-0 z-40'>
            <div
              className={`mx-auto flex flex-row space-x-2 w-full ${
                activeStep === 0 ? 'justify-end' : 'justify-between'
              }`}
            >
              {activeStep !== 0 && (
                <MyButton
                  className='w-full sm:w-auto'
                  variant='redpink-anthracite'
                  onClick={handleBack}
                >
                  {t('create-ad.previous')}
                </MyButton>
              )}
              {activeStep === steps.length - 1 ? (
                <>
                  {hasExistingAnnounce ? (
                    <MyButton
                      className='w-full sm:w-auto'
                      variant='green'
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <div className='flex items-center justify-center gap-2'>
                          <svg
                            className='animate-spin h-5 w-5 text-white'
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                          >
                            <circle
                              className='opacity-25'
                              cx='12'
                              cy='12'
                              r='10'
                              stroke='currentColor'
                              strokeWidth='4'
                            ></circle>
                            <path
                              className='opacity-75'
                              fill='currentColor'
                              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                            ></path>
                          </svg>
                          {t('saving')}
                        </div>
                      ) : (
                        t('create-ad.modify-ad')
                      )}
                    </MyButton>
                  ) : (
                    <MyButton
                      className='w-full sm:w-auto'
                      variant={isStepValid ? 'green' : 'redpink'}
                      disabled={!isStepValid}
                      onClick={handleSubmit}
                    >
                      {t('create-ad.post-ad')}
                    </MyButton>
                  )}
                </>
              ) : (
                <MyButton
                  className='w-full sm:w-auto'
                  variant={isStepValid ? 'redpink-anthracite' : 'redpink'}
                  disabled={!isStepValid}
                  onClick={handleNext}
                >
                  {t('create-ad.next')}
                </MyButton>
              )}
            </div>
          </div>
        </form>
      </ThemeProvider>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <div className='bg-white rounded-lg'>
          <h2 className='bg-anthracite text-white p-5 font-semibold text-xl text-center'>
            {t('announce-status')}
          </h2>
          <div className='space-y-4 p-5'>
            {submissionStatus === 'error' && validationErrors.length > 0 ? (
              <div>
                <p className='text-anthracite bg-grisclair rounded-lg py-2 mb-2'>
                  {t('create-ad.form-validation-failed')}
                </p>
                {renderValidationErrors()}
              </div>
            ) : (
              <p
                className={`text-center rounded-lg p-2 mb-4 ${
                  submissionStatus === 'success'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-redlight text-red-800'
                }`}
              >
                {submissionStatus === 'success'
                  ? t('announce-successfully-updated')
                  : t('announce-error-updated')}
              </p>
            )}

            <div className='flex justify-end'>
              <button
                onClick={handleCloseModal}
                className='bg-anthracite text-white px-4 py-2 rounded hover:bg-opacity-90'
              >
                {submissionStatus === 'error' && validationErrors.length > 0
                  ? t('create-ad.correct-errors')
                  : t('close')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default FormWizard
